// Which brand and which resources a page shows, from hostname / query / path. Presentation only:
// tenant authorization stays server-side (CONTRACT-QUESTIONS.md Q7).

export const BRANDS = ['solred', 'rapidshare', 'lolaup', 'uptocash', 'marketcat-drive'];

const ALIASES = {
  solred: 'solred', rapidshare: 'rapidshare', 'rapidshare.co': 'rapidshare', lolaup: 'lolaup', 'lolaup.com': 'lolaup', 'lolaup.co': 'lolaup',
  uptocash: 'uptocash', 'upto.cash': 'uptocash', upto: 'uptocash', 'marketcat-drive': 'marketcat-drive', marketcat: 'marketcat-drive', drive: 'marketcat-drive', 'drive.marketcat.io': 'marketcat-drive',
};

/** Demo host first labels (infra/demo-hosts.json, Addendum A Q7). Host → tenant is authoritative server-side. */
export const DEMO_LABELS = Object.freeze({
  demo: 'solred', 'rapidshare-demo': 'rapidshare', 'lolaup-demo': 'lolaup', 'uptocash-demo': 'uptocash',
  'drive-demo': 'marketcat-drive', 'admin-demo': 'admin', 'drive-upgrade': 'drive-upgrade',
});
const LABEL_OF = Object.fromEntries(Object.entries(DEMO_LABELS).map(([l, t]) => [t, l]));

/** Tenant served by this host: a content brand, `admin`, `drive-upgrade` or null. */
export function hostTenant(hostname) {
  const h = String(hostname || '').toLowerCase();
  const label = h.split('.')[0];
  if (DEMO_LABELS[label] && h.includes('.')) return DEMO_LABELS[label];
  for (const [re, b] of HOST_RULES) if (re.test(h)) return b;
  return null;
}

/**
 * Origin of another demo tenant next to this one (`rapidshare-demo.<suffix>` from `demo.<suffix>`), or the
 * configured brand host. Null when this page is not on a demo host family (same-origin design preview only).
 */
export function siblingOrigin(target, loc, { brandHosts = {} } = {}) {
  if (brandHosts[target]) return String(brandHosts[target]).replace(/\/$/, '');
  const h = String(loc.hostname || '').toLowerCase(), parts = h.split('.');
  if (!DEMO_LABELS[parts[0]] || parts.length < 2 || !LABEL_OF[target]) return null;
  return `${loc.protocol}//${[LABEL_OF[target], ...parts.slice(1)].join('.')}${loc.port ? `:${loc.port}` : ''}`;
}

/** Link that opens a file of the TARGET host's own catalog (resource ids are never reused across tenants). */
export function siteLink(brand, loc, cfg = {}) {
  const o = siblingOrigin(brand, loc, cfg);
  return o ? `${o}/?open=first` : `/?brand=${encodeURIComponent(brand)}&open=first`;
}

export function uploadLink(brand, loc, cfg = {}) {
  const o = siblingOrigin(brand, loc, cfg);
  return o ? `${o}/?mode=upload` : `/?brand=${encodeURIComponent(brand)}&mode=upload`;
}

/** Admin lives only on the admin host (Addendum B); same-origin /admin/ on a content host is denied by the API. */
export function adminLink(loc, cfg = {}) {
  const o = siblingOrigin('admin', loc, cfg);
  return o ? `${o}/admin/` : '/admin/';
}

// Order matters: a demo host such as `rapidshare.solred.app` must resolve to Rapidshare, not Solred.
const HOST_RULES = [
  [/(^|[.-])(drive[.-])?marketcat([.-]|$)|^drive[.-]/, 'marketcat-drive'],
  [/(^|[.-])rapidshare([.-]|$)/, 'rapidshare'],
  [/(^|[.-])lolaup([.-]|$)/, 'lolaup'],
  [/(^|[.-])(upto[.-]?cash|uptocash)([.-]|$)/, 'uptocash'],
  [/(^|[.-])solred([.-]|$)/, 'solred'],
];

/** @param {{hostname:string, search:string}} loc */
export function resolveBrand(loc) {
  const q = new URLSearchParams(loc.search).get('brand');
  if (q && ALIASES[q.toLowerCase()]) return ALIASES[q.toLowerCase()];
  const t = hostTenant(loc.hostname);
  return BRANDS.includes(t) ? t : null;
}

/** `?open=first`: the page picks a public file from its own host catalog. */
export const wantsFirstFile = (loc) => new URLSearchParams(loc.search).get('open') === 'first';

const ID = /^[A-Za-z0-9_-]{1,128}$/;

/** Resource ids from `?r=a,b` or `/d/<id>`, `/f/<id>`, `/t/<id>`, `/file/<id>`. Invalid ids are dropped. */
export function resolveResources(loc) {
  const q = new URLSearchParams(loc.search).get('r');
  let ids = q ? q.split(',') : [];
  if (!ids.length) {
    const m = /^\/(?:d|f|t|file)\/([^/?#]+)\/?$/.exec(loc.pathname || '');
    if (m) ids = [decodeURIComponent(m[1])];
  }
  return [...new Set(ids.map((s) => s.trim()).filter((s) => ID.test(s)))].slice(0, 20);
}

/** `upload` for the send/publish views, otherwise `file`. */
export function resolveMode(loc) {
  const q = new URLSearchParams(loc.search).get('mode');
  if (q === 'upload' || q === 'send' || q === 'publish') return 'upload';
  return /^\/(upload|send|publish)\/?$/.test(loc.pathname || '') ? 'upload' : 'file';
}
